import { Button, Divider, Flex, Form, Modal } from 'antd'
import { useUnit } from 'effector-react'
import { DebtForm } from 'src/pages/finSystem/debt/atoms'
import { BASE_URL } from 'src/pages/finSystem/debt/const'
import { debtListLoad, submitEditDebtFx } from 'src/pages/finSystem/debt/model'
import { DebtPayload, ListRequest } from 'src/pages/finSystem/debt/types'
import dayjs from 'dayjs'

type Props = {
  initialValues: Partial<DebtPayload> & { id: number }
  isOpen: boolean
  onClose: () => void
  filters: Record<string, any>
}

export const EditDebtModal = ({
  isOpen,
  onClose,
  initialValues,
  filters,
}: Props) => {
  const {
    id,
    walletIds,
    dateFrom,
    dateTo,
    userId,
    statuses,
    sorterField,
    page,
    pageSize,
  } = filters
  const [form] = Form.useForm()

  const loadingButton = useUnit(submitEditDebtFx.pending)

  const formWallet = Form.useWatch('wallet', form)
  const formDebtAmount = Form.useWatch('debtAmount', form)
  const formRecoveredAmount = Form.useWatch('recoveredAmount', form)
  const formCurrency = Form.useWatch('currency', form)
  const formExchangeRate = Form.useWatch('exchangeRate', form)
  const formUserId = Form.useWatch('userId', form)
  const formDate = Form.useWatch('date', form)
  const formTime = Form.useWatch('time', form)
  const formDateTime =
    formDate && formTime
      ? `${dayjs(formDate).format('YYYY-MM-DD')} ${dayjs(formTime).format(
          'HH:mm:ss',
        )}`
      : undefined
  const formComment = Form.useWatch('comment', form)
  const formStatus = Form.useWatch('status', form)

  const isSame =
    initialValues.wallet === formWallet &&
    initialValues.debtAmount === formDebtAmount &&
    initialValues.recoveredAmount === formRecoveredAmount &&
    initialValues.currency === formCurrency &&
    initialValues.exchangeRate === formExchangeRate &&
    initialValues.userId === formUserId &&
    initialValues.dateTime === formDateTime &&
    initialValues.comment === formComment &&
    initialValues.status === formStatus

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      const body: Partial<DebtPayload> = {}
      if (values.wallet !== initialValues.wallet) {
        body['wallet'] = values.wallet
      }
      if (values.debtAmount !== initialValues.debtAmount) {
        body['debtAmount'] = values.debtAmount
      }
      if (values.recoveredAmount !== initialValues.recoveredAmount) {
        body['recoveredAmount'] = values.recoveredAmount
      }
      if (values.currency !== initialValues.currency) {
        body['currency'] = values.currency
      }
      if (values.exchangeRate !== initialValues.exchangeRate) {
        body['exchangeRate'] = values.exchangeRate
      }
      if (values.userId !== initialValues.userId) {
        body['userId'] = values.userId
      }
      if (formDateTime !== initialValues.dateTime) {
        body['dateTime'] = formDateTime
      }
      if (values.comment !== initialValues.comment) {
        body['comment'] = values.comment
      }
      if (values.status !== initialValues.status) {
        body['status'] = values.status
      }

      await submitEditDebtFx({
        url: `${BASE_URL}/${initialValues.id}`,
        body,
      })
      handleCancel()
      debtListLoad({
        id,
        walletIds,
        dateFrom,
        dateTo,
        userId,
        statuses,
        sorterField,
        page,
        pageSize,
      } as ListRequest)
    } catch (error) {
      console.error(error)
    }
  }

  const handleCancel = () => {
    onClose()
    form.resetFields()
  }

  return (
    <Modal
      title={<div style={{ fontSize: 20 }}>Edit Debt Transaction</div>}
      centered
      wrapClassName="ant_modal"
      width={500}
      maskClosable={false}
      open={isOpen}
      onCancel={handleCancel}
      footer={
        <Flex gap={8} justify="flex-end">
          <Button onClick={handleCancel}>Cancel</Button>
          <Button
            type="primary"
            onClick={handleSubmit}
            disabled={isSame}
            loading={loadingButton}
          >
            Save
          </Button>
        </Flex>
      }
    >
      <Divider />
      <DebtForm form={form} initialValues={initialValues} />
    </Modal>
  )
}
